import React, { useState } from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  HStack,
  Button,
  Input,
  FormControl,
  FormLabel,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Card,
  CardBody,
  Badge,
  Code,
  Tooltip,
  useToast,
  Spinner,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
} from '@chakra-ui/react';
import { FiSearch, FiExternalLink } from 'react-icons/fi';

function Hyperunit() {
  const [address, setAddress] = useState('');
  const [operations, setOperations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const toast = useToast();

  const fetchOperations = async () => {
    if (!address.trim()) {
      toast({
        title: 'Error',
        description: 'Please enter an address',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`https://api.hyperunit.xyz/operations/${address}`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.operations && Array.isArray(data.operations)) {
        // Sort operations by creation date (most recent first)
        const sortedOperations = data.operations.sort((a, b) => 
          new Date(b.opCreatedAt) - new Date(a.opCreatedAt)
        );
        setOperations(sortedOperations);
        
        toast({
          title: 'Success',
          description: `Found ${sortedOperations.length} operations`,
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      } else {
        setOperations([]);
        toast({
          title: 'No operations found',
          description: 'No operations found for this address',
          status: 'info',
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (err) {
      setError(err.message);
      setOperations([]);
      toast({
        title: 'Error',
        description: `Failed to fetch operations: ${err.message}`,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const formatAmount = (amount, asset) => {
    if (!amount) return 'N/A';
    
    // Convert from smallest unit to readable format
    const decimals = asset === 'eth' ? 18 : asset === 'sol' ? 9 : 6;
    const formattedAmount = (parseFloat(amount) / Math.pow(10, decimals)).toFixed(6);
    
    return `${formattedAmount} ${asset.toUpperCase()}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  const getStateColor = (state) => {
    switch (state) {
      case 'done':
        return 'green';
      case 'queuedForWithdraw':
        return 'yellow';
      case 'pending':
        return 'blue';
      case 'failed':
        return 'red';
      default:
        return 'gray';
    }
  };

  const getExplorerUrl = (txHash, chain) => {
    if (!txHash) return null;
    
    if (chain === 'solana') {
      return `https://solscan.io/tx/${txHash}`;
    } else if (chain === 'ethereum') {
      return `https://etherscan.io/tx/${txHash}`;
    } else if (chain === 'hyperliquid') {
      return `https://explorer.hyperliquid.xyz/tx/${txHash}`;
    }
    return null;
  };

  const copyToClipboard = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      toast({
        title: 'Copied!',
        description: `${label} copied to clipboard`,
        status: 'success',
        duration: 2000,
        isClosable: true,
      });
    } catch (err) {
      toast({
        title: 'Copy failed',
        description: 'Failed to copy to clipboard',
        status: 'error',
        duration: 2000,
        isClosable: true,
      });
    }
  };

  return (
    <Box minH="100vh" bg="gray.50">
      <Container maxW="full" px={6} py={6}>
        <VStack spacing={6} align="stretch">
          {/* Header */}
          <Box>
            <Heading size="lg" color="gray.900" fontWeight="bold" mb={2}>
              Hyperunit Operations
            </Heading>
            <Text color="gray.600" fontSize="sm">
              Track cross-chain operations and bridge transactions
            </Text>
          </Box>

          {/* Search Form */}
          <Card bg="white" borderRadius="md" border="1px" borderColor="gray.200">
            <CardBody p={6}>
              <VStack spacing={4} align="stretch">
                <FormControl>
                  <FormLabel fontSize="sm" color="gray.700" fontWeight="medium">
                    Address
                  </FormLabel>
                  <HStack spacing={3}>
                    <Input
                      placeholder="Enter wallet address (Solana, Ethereum, or Hyperliquid)"
                      value={address}
                      onChange={(e) => setAddress(e.target.value)}
                      size="md"
                      bg="white"
                      borderColor="gray.300"
                      color="gray.900"
                      _focus={{ borderColor: "green.500" }}
                      _placeholder={{ color: "gray.500" }}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          fetchOperations();
                        }
                      }}
                    />
                    <Button
                      colorScheme="green"
                      size="md"
                      onClick={fetchOperations}
                      isLoading={loading}
                      loadingText="Searching..."
                      leftIcon={<FiSearch />}
                      isDisabled={!address.trim()}
                    >
                      Search
                    </Button>
                  </HStack>
                </FormControl>
              </VStack>
            </CardBody>
          </Card>

          {/* Error State */}
          {error && (
            <Alert status="error" borderRadius="md">
              <AlertIcon />
              <Box>
                <AlertTitle fontSize="md">Error loading operations!</AlertTitle>
                <AlertDescription fontSize="sm">{error}</AlertDescription>
              </Box>
            </Alert>
          )}

          {/* Loading State */}
          {loading && (
            <Box textAlign="center" py={8}>
              <Spinner size="lg" color="green.500" />
              <Text mt={4} color="gray.600">Fetching operations...</Text>
            </Box>
          )}

          {/* Operations Table */}
          {operations.length > 0 && !loading && (
            <Box>
              <HStack spacing={2} mb={4}>
                <Heading size="md" color="gray.800" fontWeight="semibold">
                  Operations
                </Heading>
                <Badge colorScheme="green" variant="subtle" fontSize="xs">
                  {operations.length}
                </Badge>
              </HStack>
              
              <Box bg="white" borderRadius="md" border="1px" borderColor="gray.200">
                <TableContainer>
                  <Table variant="simple" size="sm">
                    <Thead bg="gray.50">
                      <Tr>
                        <Th fontSize="xs" fontWeight="semibold" color="gray.700" py={3}>
                          Created
                        </Th>
                        <Th fontSize="xs" fontWeight="semibold" color="gray.700" py={3}>
                          Source Wallet
                        </Th>
                        <Th fontSize="xs" fontWeight="semibold" color="gray.700" py={3}>
                          Destination Wallet
                        </Th>
                        <Th fontSize="xs" fontWeight="semibold" color="gray.700" py={3}>
                          Source Chain
                        </Th>
                        <Th fontSize="xs" fontWeight="semibold" color="gray.700" py={3}>
                          Destination Chain
                        </Th>
                        <Th fontSize="xs" fontWeight="semibold" color="gray.700" py={3} isNumeric>
                          Amount
                        </Th>
                        <Th fontSize="xs" fontWeight="semibold" color="gray.700" py={3}>
                          State
                        </Th>
                        <Th fontSize="xs" fontWeight="semibold" color="gray.700" py={3}>
                          Transactions
                        </Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {operations.map((operation, index) => (
                        <Tr key={index} _hover={{ bg: 'gray.50' }}>
                          <Td py={3}>
                            <Text fontSize="xs" color="gray.600">
                              {formatDate(operation.opCreatedAt)}
                            </Text>
                          </Td>
                          <Td py={3}>
                            <Tooltip label={`Click to copy: ${operation.sourceAddress || 'N/A'}`} placement="top">
                              <Code 
                                fontSize="xs" 
                                colorScheme="blue" 
                                p={2} 
                                borderRadius="sm" 
                                cursor="pointer"
                                color="blue.800"
                                bg="blue.50"
                                maxW="200px"
                                overflow="hidden"
                                textOverflow="ellipsis"
                                whiteSpace="nowrap"
                                onClick={() => operation.sourceAddress && copyToClipboard(operation.sourceAddress, 'Source wallet')}
                                _hover={{ bg: 'blue.100' }}
                              >
                                {operation.sourceAddress ? `${operation.sourceAddress.slice(0, 8)}...${operation.sourceAddress.slice(-8)}` : 'N/A'}
                              </Code>
                            </Tooltip>
                          </Td>
                          <Td py={3}>
                            <Tooltip label={`Click to copy: ${operation.destinationAddress || 'N/A'}`} placement="top">
                              <Code 
                                fontSize="xs" 
                                colorScheme="purple" 
                                p={2} 
                                borderRadius="sm" 
                                cursor="pointer"
                                color="purple.800"
                                bg="purple.50"
                                maxW="200px"
                                overflow="hidden"
                                textOverflow="ellipsis"
                                whiteSpace="nowrap"
                                onClick={() => operation.destinationAddress && copyToClipboard(operation.destinationAddress, 'Destination wallet')}
                                _hover={{ bg: 'purple.100' }}
                              >
                                {operation.destinationAddress ? `${operation.destinationAddress.slice(0, 8)}...${operation.destinationAddress.slice(-8)}` : 'N/A'}
                              </Code>
                            </Tooltip>
                          </Td>
                          <Td py={3}>
                            <Badge colorScheme="blue" variant="subtle" fontSize="xs">
                              {operation.sourceChain}
                            </Badge>
                          </Td>
                          <Td py={3}>
                            <Badge colorScheme="purple" variant="subtle" fontSize="xs">
                              {operation.destinationChain}
                            </Badge>
                          </Td>
                          <Td isNumeric py={3}>
                            <Text fontWeight="medium" color="gray.900" fontSize="sm">
                              {formatAmount(operation.sourceAmount, operation.asset)}
                            </Text>
                          </Td>
                          <Td py={3}>
                            <Badge 
                              colorScheme={getStateColor(operation.state)} 
                              variant="subtle" 
                              fontSize="xs"
                            >
                              {operation.state}
                            </Badge>
                          </Td>
                          <Td py={3}>
                            <HStack spacing={2}>
                              {operation.sourceTxHash && (
                                <Tooltip label="View source transaction" placement="top">
                                  <Button
                                    size="xs"
                                    variant="outline"
                                    colorScheme="blue"
                                    leftIcon={<FiExternalLink />}
                                    onClick={() => {
                                      const url = getExplorerUrl(operation.sourceTxHash, operation.sourceChain);
                                      if (url) window.open(url, '_blank');
                                    }}
                                  >
                                    Source
                                  </Button>
                                </Tooltip>
                              )}
                              {operation.destinationTxHash && (
                                <Tooltip label="View destination transaction" placement="top">
                                  <Button
                                    size="xs"
                                    variant="outline"
                                    colorScheme="purple"
                                    leftIcon={<FiExternalLink />}
                                    onClick={() => {
                                      const url = getExplorerUrl(operation.destinationTxHash, operation.destinationChain);
                                      if (url) window.open(url, '_blank');
                                    }}
                                  >
                                    Dest
                                  </Button>
                                </Tooltip>
                              )}
                            </HStack>
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </TableContainer>
              </Box>
            </Box>
          )}

          {/* No Operations State */}
          {operations.length === 0 && !loading && !error && address && (
            <Box textAlign="center" py={8}>
              <Text color="gray.500" fontSize="md">
                No operations found for this address
              </Text>
            </Box>
          )}

          {/* Instructions */}
          {!address && (
            <Card bg="white" borderRadius="md" border="1px" borderColor="gray.200">
              <CardBody p={6}>
                <VStack spacing={4} align="start">
                  <Heading size="md" color="gray.800" fontWeight="semibold">
                    How to use Hyperunit Operations
                  </Heading>
                  <VStack spacing={3} align="start">
                    <Text fontSize="sm" color="gray.600">
                      1. Enter a wallet address from Solana, Ethereum, or Hyperliquid
                    </Text>
                    <Text fontSize="sm" color="gray.600">
                      2. Click "Search Operations" to fetch all cross-chain operations
                    </Text>
                    <Text fontSize="sm" color="gray.600">
                      3. View transaction details and click on transaction links to explore on block explorers
                    </Text>
                    <Text fontSize="sm" color="gray.600">
                      4. Operations are sorted by creation date (most recent first)
                    </Text>
                  </VStack>
                </VStack>
              </CardBody>
            </Card>
          )}
        </VStack>
      </Container>
    </Box>
  );
}

export default Hyperunit;
